// Performance tracking utility for measuring load times
import { createLogger } from '$lib/utils/logger';

const logger = createLogger('performance.tracker');

export interface PerformanceStage {
	name: string;
	startTime: number;
	endTime?: number;
	duration?: number;
	metadata?: Record<string, any>;
}

export interface PerformanceSession {
	sessionId: string;
	userId?: string;
	startTime: number;
	stages: Map<string, PerformanceStage>;
	completedAt?: number;
	totalDuration?: number;
}

class PerformanceTracker {
	private currentSession: PerformanceSession | null = null;
	private readonly STORAGE_KEY = 'perf_sessions';
	private readonly MAX_SESSIONS = 10;

	/**
	 * Start a new performance tracking session
	 */
	startSession(userId?: string): string {
		const sessionId = `perf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

		this.currentSession = {
			sessionId,
			userId,
			startTime: performance.now(),
			stages: new Map()
		};

		logger.debug('Performance session started', {
			sessionId,
			userId,
			timestamp: new Date().toISOString()
		});

		return sessionId;
	}

	/**
	 * Start tracking a specific stage
	 */
	startStage(stageName: string, metadata?: Record<string, any>): void {
		if (!this.currentSession) {
			logger.warn('No active session, starting new one for stage', { stageName });
			this.startSession();
		}

		const stage: PerformanceStage = {
			name: stageName,
			startTime: performance.now(),
			metadata
		};

		this.currentSession!.stages.set(stageName, stage);

		logger.debug('Performance stage started', {
			stageName,
			sessionId: this.currentSession!.sessionId,
			relativeTime: this.getRelativeTime(),
			metadata
		});
	}

	/**
	 * End tracking for a specific stage
	 */
	endStage(stageName: string, metadata?: Record<string, any>): number | null {
		if (!this.currentSession) {
			logger.warn('No active session for stage end', { stageName });
			return null;
		}

		const stage = this.currentSession.stages.get(stageName);
		if (!stage) {
			logger.warn('Stage not found', { stageName });
			return null;
		}

		stage.endTime = performance.now();
		stage.duration = stage.endTime - stage.startTime;

		if (metadata) {
			stage.metadata = { ...stage.metadata, ...metadata };
		}

		logger.info('Performance stage completed', {
			stageName,
			sessionId: this.currentSession.sessionId,
			durationMs: stage.duration.toFixed(2),
			relativeTime: this.getRelativeTime(),
			metadata: stage.metadata
		});

		return stage.duration;
	}

	/**
	 * End the current session and save results
	 */
	endSession(): void {
		if (!this.currentSession) {
			logger.warn('No active session to end');
			return;
		}

		this.currentSession.completedAt = performance.now();
		this.currentSession.totalDuration =
			this.currentSession.completedAt - this.currentSession.startTime;

		// Log summary
		this.logSessionSummary();

		// Save to localStorage for analysis
		this.saveSession();

		// Clear current session
		this.currentSession = null;
	}

	/**
	 * Log a summary of the current session
	 */
	private logSessionSummary(): void {
		if (!this.currentSession) return;

		const stages = Array.from(this.currentSession.stages.values());
		const completedStages = stages.filter((s) => s.duration !== undefined);
		const criticalMetrics = this.getCriticalMetrics();

		const stagesSummary = completedStages.map((stage) => ({
			name: stage.name,
			durationMs: stage.duration?.toFixed(2),
			percentage: ((stage.duration! / this.currentSession!.totalDuration!) * 100).toFixed(1)
		}));

		logger.info('Performance session summary', {
			sessionId: this.currentSession.sessionId,
			totalDurationMs: this.currentSession.totalDuration?.toFixed(2),
			stagesCompleted: `${completedStages.length}/${stages.length}`,
			stages: stagesSummary,
			criticalMetrics
		});
	}

	/**
	 * Get critical performance metrics
	 */
	getCriticalMetrics(): Record<string, string> {
		if (!this.currentSession) return {};

		const stages = this.currentSession.stages;
		const metrics: Record<string, string> = {};

		// Time to first data
		const authStage = stages.get('auth:login');
		const firstDataStage =
			stages.get('data:alerts:received') || stages.get('data:workorders:received');

		if (authStage?.duration) {
			metrics['Login Time'] = `${authStage.duration.toFixed(2)}ms`;
		}

		if (firstDataStage?.endTime && authStage?.startTime) {
			const timeToFirstData = firstDataStage.endTime - authStage.startTime;
			metrics['Time to First Data'] = `${timeToFirstData.toFixed(2)}ms`;
		}

		// WebSocket connection time
		const wsStage = stages.get('websocket:connected');
		if (wsStage?.duration) {
			metrics['WebSocket Connection'] = `${wsStage.duration.toFixed(2)}ms`;
		}

		// Total data load time
		const alertsStage = stages.get('data:alerts:received');
		const workOrdersStage = stages.get('data:workorders:received');

		if (alertsStage?.duration) {
			metrics['Alerts Load Time'] = `${alertsStage.duration.toFixed(2)}ms`;
		}

		if (workOrdersStage?.duration) {
			metrics['Work Orders Load Time'] = `${workOrdersStage.duration.toFixed(2)}ms`;
		}

		return metrics;
	}

	/**
	 * Save session to localStorage
	 */
	private saveSession(): void {
		if (!this.currentSession) return;

		try {
			const stored = localStorage.getItem(this.STORAGE_KEY);
			let sessions: any[] = stored ? JSON.parse(stored) : [];

			// Convert Map to array for storage
			const sessionData = {
				...this.currentSession,
				stages: Array.from(this.currentSession.stages.entries()).map(([key, value]) => ({
					key,
					...value
				}))
			};

			sessions.unshift(sessionData);

			// Keep only recent sessions
			if (sessions.length > this.MAX_SESSIONS) {
				sessions = sessions.slice(0, this.MAX_SESSIONS);
			}

			localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessions));
		} catch (error) {
			logger.error('Failed to save performance session to localStorage', { error });
		}
	}

	/**
	 * Get relative time since session start
	 */
	private getRelativeTime(): string {
		if (!this.currentSession) return '0ms';
		const elapsed = performance.now() - this.currentSession.startTime;
		return `+${elapsed.toFixed(2)}ms`;
	}

	/**
	 * Get all stored sessions for analysis
	 */
	getStoredSessions(): any[] {
		try {
			const stored = localStorage.getItem(this.STORAGE_KEY);
			return stored ? JSON.parse(stored) : [];
		} catch {
			return [];
		}
	}

	/**
	 * Clear all stored sessions
	 */
	clearStoredSessions(): void {
		localStorage.removeItem(this.STORAGE_KEY);
		logger.debug('Stored performance sessions cleared');
	}

	/**
	 * Get current session (for debugging)
	 */
	getCurrentSession(): PerformanceSession | null {
		return this.currentSession;
	}
}

// Export singleton instance
export const performanceTracker = new PerformanceTracker();

// Export convenience functions
export function startPerfSession(userId?: string): string {
	return performanceTracker.startSession(userId);
}

export function trackStage(stageName: string, metadata?: Record<string, any>): void {
	performanceTracker.startStage(stageName, metadata);
}

export function endStage(stageName: string, metadata?: Record<string, any>): number | null {
	return performanceTracker.endStage(stageName, metadata);
}

export function endPerfSession(): void {
	performanceTracker.endSession();
}

export function getPerfMetrics(): Record<string, string> {
	return performanceTracker.getCriticalMetrics();
}
